/**
 * The task drawer's state (DD-051): closed, one task in view or edit mode,
 * or a new task in create mode. One drawer per list, owned by the list
 * through useTaskDrawer. Pure, so jest covers every transition.
 */

import type { VoiceRecording } from "@/core/voice";
import type { Task, TaskLinkRef, TaskPerson } from "../types";
import { TaskDraft, changeDraft, draftFromTask, emptyDraft, moveDraft } from "./taskDrawerForm";

/** What create mode starts from: the list's client (null on the Tasks tab,
 * where the form asks), the list's item as the starting links, and the
 * people to start tagged (the Home panel's client contacts, DD-053). */
export interface TaskCreateDefaults {
  clientId: string | null;
  links: TaskLinkRef[];
  people?: TaskPerson[];
}

/** `session` counts the view, edit and create sessions the drawer has
 * started. It never resets, so a save or create response that lands after the
 * drawer moved on can be told apart from one for the live session. A draft has
 * no task id to send a voice note to, so create mode holds the stopped
 * recording, and the create hands it to view mode as `firstNote`, which the
 * notes section sends once and then takes. */
export type TaskDrawerState =
  | { mode: "closed"; session: number }
  | { mode: "view"; session: number; task: Task; confirmingDelete: boolean; firstNote: VoiceRecording | null }
  | { mode: "edit"; session: number; task: Task; draft: TaskDraft }
  | {
      mode: "create";
      session: number;
      createDefaults: TaskCreateDefaults;
      draft: TaskDraft;
      recording: VoiceRecording | null;
    };

export type TaskDrawerAction =
  | { type: "open"; task: Task }
  | { type: "create"; defaults: TaskCreateDefaults }
  | { type: "edit" }
  | { type: "draft"; patch: Partial<TaskDraft> }
  | { type: "recording"; recording: VoiceRecording | null }
  | { type: "firstNoteTaken" }
  | { type: "cancel" }
  | { type: "saved"; task: Task; session: number }
  | { type: "created"; task: Task; session: number }
  | { type: "synced"; task: Task }
  | { type: "confirmDelete" }
  | { type: "cancelDelete" }
  | { type: "close" };

export const CLOSED: TaskDrawerState = { mode: "closed", session: 0 };

const viewOf = (task: Task, session: number, firstNote: VoiceRecording | null = null): TaskDrawerState => ({
  mode: "view",
  session,
  task,
  confirmingDelete: false,
  firstNote,
});

/** A cached detail from before a later write must not overwrite the held
 * task. Missing or equal stamps apply. */
function isOlder(incoming: Task, held: Task): boolean {
  if (!incoming.updatedAt || !held.updatedAt) return false;
  return Date.parse(incoming.updatedAt) < Date.parse(held.updatedAt);
}

export function taskDrawerReducer(state: TaskDrawerState, action: TaskDrawerAction): TaskDrawerState {
  switch (action.type) {
    case "open":
      return viewOf(action.task, state.session + 1);
    case "create":
      return {
        mode: "create",
        session: state.session + 1,
        createDefaults: action.defaults,
        draft: emptyDraft(action.defaults.clientId, action.defaults.people),
        recording: null,
      };
    case "edit":
      return state.mode === "view"
        ? { mode: "edit", session: state.session + 1, task: state.task, draft: draftFromTask(state.task) }
        : state;
    case "draft":
      // Edit mode knows the saved owner, whom a new owner keeps on as a tag (TD-103).
      if (state.mode === "edit") {
        const { clientId } = action.patch;
        if (clientId != null && clientId !== state.draft.clientId) {
          return { ...state, draft: moveDraft(state.draft, clientId, state.task) };
        }
        return { ...state, draft: changeDraft(state.draft, action.patch, state.task.assignee) };
      }
      if (state.mode === "create") return { ...state, draft: changeDraft(state.draft, action.patch) };
      return state;
    case "recording":
      return state.mode === "create" ? { ...state, recording: action.recording } : state;
    case "firstNoteTaken":
      return state.mode === "view" && state.firstNote !== null ? { ...state, firstNote: null } : state;
    case "cancel":
      if (state.mode === "edit") return viewOf(state.task, state.session);
      if (state.mode === "create") return { mode: "closed", session: state.session };
      return state;
    case "saved":
      // A response from an earlier session changes nothing.
      return state.mode === "edit" && state.session === action.session && state.task.id === action.task.id
        ? viewOf(action.task, state.session)
        : state;
    case "created":
      return state.mode === "create" && state.session === action.session
        ? viewOf(action.task, state.session, state.recording)
        : state;
    case "synced":
      return (state.mode === "view" || state.mode === "edit") &&
        state.task.id === action.task.id &&
        !isOlder(action.task, state.task)
        ? { ...state, task: action.task }
        : state;
    case "confirmDelete":
      return state.mode === "view" ? { ...state, confirmingDelete: true } : state;
    case "cancelDelete":
      return state.mode === "view" ? { ...state, confirmingDelete: false } : state;
    case "close":
      return { mode: "closed", session: state.session + 1 };
  }
}
